import { useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ProtectedRoute } from '@/core/components/routing/ProtectedRoute';
import { AuthOnlyRoute } from '@/core/components/routing/AuthOnlyRoute';
import { AuthErrorScreen } from '@/core/components/auth/AuthErrorScreen';
import { AuthLoadingScreen } from '@/core/components/loading/AuthLoadingScreen';

/**
 * RootRedirect - Handles "/" path
 * In iframe mode, refresh once to let parent app set the correct URL.
 * In standalone mode, redirect to energy-management.
 */
const RootRedirect = () => {
  useEffect(() => {
    const isInIframe = window.self !== window.top;
    if (isInIframe) {
      // In iframe mode, refresh once to let parent set the correct URL
      // Use sessionStorage to prevent infinite loop
      const hasRefreshed = sessionStorage.getItem('root-refresh-attempted');
      if (!hasRefreshed) {
        sessionStorage.setItem('root-refresh-attempted', 'true');
        window.location.reload();
      } else {
        // Already refreshed once, clear flag and redirect to default page
        sessionStorage.removeItem('root-refresh-attempted');
        window.location.href = '/energy-management';
      }
    } else {
      // Standalone mode: redirect to energy-management as default
      window.location.href = '/energy-management';
    }
  }, []);

  return <AuthLoadingScreen />;
};
import { SiteManagementDashboard } from '@/features/site-management/components/SiteManagementDashboard';
import { OperatorDashboard } from '@/features/operator-dashboard/OperatorDashboard';
import KioskReports from '@/features/kiosk-sessions/KioskReports';
import { SessionsReport } from '@/features/sessions-report/SessionsReport';
import { OcpiSettings } from '@/features/ocpi-settings/OcpiSettings';
import { Manufacturers } from '@/features/manufacturers/Manufacturers';
import { CommunitiesPage } from '@/features/communities/components/pages/CommunitiesPage';
import { MachineSetupPage } from '@/features/machine-setup/components/pages/MachineSetupPage';
import { MachineSetupSettingsPage } from '@/features/machine-setup/components/pages/MachineSetupSettingsPage';
import { KioskFlowManagementPage } from '@/features/kiosk-flow-management/components/pages/KioskFlowManagementPage';
import { Translations } from '@/features/translations/Translations';
import { BulkImportPage } from '@/features/translations/components/pages/BulkImportPage';
import { PublishPage } from '@/features/translations/components/pages/PublishPage';
import { VersionHistoryPage } from '@/features/translations/components/pages/VersionHistoryPage';
import { LanguageManagement } from '@/features/translations/components/pages/LanguageManagement';
import { AppLanguagesConfig } from '@/features/translations/components/pages/AppLanguagesConfig';
import { SoundManagement } from '@/features/translations/components/pages/SoundManagement';
import { WeezmoManagement } from '@/features/translations/components/pages/WeezmoManagement';
import { ImportConfiguration } from '@/features/translations/components/pages/ImportConfiguration';
import { AssetManagement } from '@/features/asset-management/AssetManagement';
import { ApiTokens } from '@/features/api-tokens/ApiTokens';
import { AdminMenu } from '@/features/admin/components/AdminMenu';
import { PopupPlayground } from '@/features/popup-playground/PopupPlayground';
import { GeneralSettingsPage } from '@/features/general-settings/components/pages/GeneralSettingsPage';
import { KioskCustomizerPage } from '@/features/device-customizer/kiosk/components/KioskCustomizerPage';
import { KioskPreviewDemoPage } from '@/features/device-customizer/kiosk/pages/KioskPreviewDemoPage';
import { AssignKioskPage } from '@/features/assign-kiosk/components/pages/AssignKioskPage';
import NotFound from '@/core/components/NotFound';
import { LoginPage } from '@/core/components/LoginPage';
import { BillingConfigPage } from '@/features/billing/components/pages/BillingConfigPage';
import DataErrorPage from '@/features/data-error/components/pages/DataErrorPage';

/**
 * AppRoutes - Route Configuration Component
 * Defines all application routes
 */
export const AppRoutesConfig = () => {
  return (
    <Routes>
      {/* Root path refreshes to let parent iframe set correct URL */}
      <Route path="/" element={<RootRedirect />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <div className="h-screen overflow-hidden">
            <OperatorDashboard />
          </div>
        </ProtectedRoute>
      } />
      <Route path="/energy-management" element={
        <ProtectedRoute>
          <div className="min-h-screen bg-gray-50">
            <SiteManagementDashboard />
          </div>
        </ProtectedRoute>
      } />
      <Route path="/kiosk-reports" element={
        <ProtectedRoute>
          <div className="fixed inset-0 top-8 flex flex-col overflow-hidden bg-gray-50">
            <KioskReports />
          </div>
        </ProtectedRoute>
      } />
      <Route path="/data-error" element={
        <ProtectedRoute>
          <div className="fixed inset-0 top-8 flex flex-col overflow-hidden bg-gray-50">
            <DataErrorPage />
          </div>
        </ProtectedRoute>
      } />
      <Route path="/sessions-report" element={
        <ProtectedRoute>
          <div className="fixed inset-0 top-8 flex flex-col overflow-hidden">
            <SessionsReport />
          </div>
        </ProtectedRoute>
      } />
      <Route path="/ocpi-admin" element={
        <AuthOnlyRoute>
          <div className="min-h-screen bg-gray-50">
            <OcpiSettings />
          </div>
        </AuthOnlyRoute>
      } />
      <Route path="/manufacturers" element={
        <AuthOnlyRoute>
          <div className="fixed inset-0 top-8 flex flex-col overflow-hidden bg-gray-50">
            <Manufacturers />
          </div>
        </AuthOnlyRoute>
      } />
      <Route path="/communities" element={
        <ProtectedRoute>
          <div className="min-h-screen bg-gray-50">
            <CommunitiesPage />
          </div>
        </ProtectedRoute>
      } />
      <Route path="/machine-setup" element={
        <ProtectedRoute>
          <div className="min-h-screen bg-gray-50">
            <MachineSetupPage />
          </div>
        </ProtectedRoute>
      } />
      <Route path="/machine-setup/settings" element={
        <AuthOnlyRoute>
          <div className="min-h-screen bg-gray-50">
            <MachineSetupSettingsPage />
          </div>
        </AuthOnlyRoute>
      } />
      <Route path="/kiosk-flow-management" element={
        <AuthOnlyRoute>
          <div className="min-h-screen bg-gray-50">
            <KioskFlowManagementPage />
          </div>
        </AuthOnlyRoute>
      } />
      <Route path="/translations" element={
        <AuthOnlyRoute>
          <div className="min-h-screen bg-gray-50">
            <Translations />
          </div>
        </AuthOnlyRoute>
      } />
      <Route path="/translations/import" element={
        <AuthOnlyRoute>
          <div className="min-h-screen bg-gray-50">
            <BulkImportPage />
          </div>
        </AuthOnlyRoute>
      } />
      <Route path="/translations/publish" element={
        <AuthOnlyRoute>
          <div className="min-h-screen bg-gray-50">
            <PublishPage />
          </div>
        </AuthOnlyRoute>
      } />
      <Route path="/translations/versions" element={
        <AuthOnlyRoute>
          <div className="min-h-screen bg-gray-50">
            <VersionHistoryPage />
          </div>
        </AuthOnlyRoute>
      } />
      <Route path="/translations/languages" element={
        <AuthOnlyRoute>
          <div className="min-h-screen bg-gray-50">
            <AppLanguagesConfig />
          </div>
        </AuthOnlyRoute>
      } />
      <Route path="/translations/global-languages" element={
        <AuthOnlyRoute>
          <div className="min-h-screen bg-gray-50">
            <LanguageManagement />
          </div>
        </AuthOnlyRoute>
      } />
      <Route path="/translations/sounds" element={
        <AuthOnlyRoute>
          <div className="min-h-screen bg-gray-50">
            <SoundManagement />
          </div>
        </AuthOnlyRoute>
      } />
      <Route path="/translations/weezmo" element={
        <AuthOnlyRoute>
          <div className="min-h-screen bg-gray-50">
            <WeezmoManagement />
          </div>
        </AuthOnlyRoute>
      } />
      <Route path="/translations/import-config" element={
        <AuthOnlyRoute>
          <div className="min-h-screen bg-gray-50">
            <ImportConfiguration />
          </div>
        </AuthOnlyRoute>
      } />
      <Route path="/assets" element={
        <AuthOnlyRoute>
          <div className="min-h-screen bg-gray-50">
            <AssetManagement />
          </div>
        </AuthOnlyRoute>
      } />
      <Route path="/api-tokens" element={
        <AuthOnlyRoute>
          <div className="min-h-screen bg-gray-50">
            <ApiTokens />
          </div>
        </AuthOnlyRoute>
      } />
      <Route path="/operator/:operatorId/assignEv" element={
        <ProtectedRoute>
          <GeneralSettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/ev-admin" element={
        <AuthOnlyRoute>
          <div className="min-h-screen bg-gray-50">
            <AdminMenu />
          </div>
        </AuthOnlyRoute>
      } />
      <Route path="/popup-playground" element={
        <div className="min-h-screen bg-gray-50">
          <PopupPlayground />
        </div>
      } />
      <Route path="/device-customizer/kiosk" element={
        <ProtectedRoute>
          <KioskCustomizerPage />
        </ProtectedRoute>
      } />
      <Route path="/device-customizer/kiosk/:operatorId" element={
        <ProtectedRoute>
          <KioskCustomizerPage />
        </ProtectedRoute>
      } />
      <Route path="/kiosk-preview-demo" element={
        <ProtectedRoute>
          <KioskPreviewDemoPage />
        </ProtectedRoute>
      } />
      <Route path="/AssignKiosk" element={
        <ProtectedRoute>
          <AssignKioskPage />
        </ProtectedRoute>
      } />
      <Route path="/machines/:machineId/AssignKiosk" element={
        <ProtectedRoute>
          <AssignKioskPage />
        </ProtectedRoute>
      } />
      <Route path="/billing" element={
        <ProtectedRoute>
          <div className="min-h-screen bg-gray-50">
            <BillingConfigPage />
          </div>
        </ProtectedRoute>
      } />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth-error" element={<AuthErrorScreen />} />
      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};
